import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import arTranslations from './locales/ar.json'
import enTranslations from './locales/en.json'
import frTranslations from './locales/fr.json'
import trTranslations from './locales/tr.json'
import urTranslations from './locales/ur.json'
import msTranslations from './locales/ms.json'
import { SUPPORTED_LANG_CODES } from '@/utils/localeFromUrl'

// Language direction cache (populated from API)
let languageDirections: Map<string, 'ltr' | 'rtl'> = new Map([
  ['ar', 'rtl'],
  ['ur', 'rtl'],
  // Default others to ltr
])

// Function to update language directions from API
export const updateLanguageDirections = (languages: Array<{ code: string; direction: 'ltr' | 'rtl' }>) => {
  languageDirections.clear()
  languages.forEach(lang => {
    languageDirections.set(lang.code.toLowerCase(), lang.direction)
  })
}

console.log('Loading i18n resources', { ar: !!arTranslations, en: !!enTranslations });

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ar: { translation: arTranslations },
      en: { translation: enTranslations },
      fr: { translation: frTranslations },
      tr: { translation: trTranslations },
      ur: { translation: urTranslations },
      ms: { translation: msTranslations },
    },
    lng: 'ar', // Fallback when no stored preference
    fallbackLng: ['ar', 'en'],
    supportedLngs: [...SUPPORTED_LANG_CODES],
    load: 'languageOnly', // Only load 'en', not 'en-US'
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'querystring', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
  })

// Persist language to localStorage whenever it changes (so refresh keeps selection)
const STORAGE_KEY = 'i18nextLng'
i18n.on('languageChanged', (lng: string) => {
  try {
    const base = (lng || '').split('-')[0]
    if (base) localStorage.setItem(STORAGE_KEY, base)
  } catch {
    /* ignore */
  }
})

// Ensure we respect localStorage immediately on load (before any async code)
try {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
  const base = stored?.split('-')[0]
  if (base && i18n.language?.split('-')[0] !== base) {
    i18n.changeLanguage(base)
  }
} catch {
  /* ignore */
}

// RTL/LTR handling: keep html dir/lang in sync with selected language
export const applyDirection = (lng?: string, direction?: 'ltr' | 'rtl') => {
  if (typeof document === 'undefined') return
  
  const lang = lng || i18n.language || 'ar'
  // Extract base language code (e.g., 'ar' from 'ar-EG')
  const baseLang = lang.split('-')[0].toLowerCase()
  
  // Use provided direction or look it up from cache
  const dir = direction || languageDirections.get(baseLang) || 'ltr'
  
  const html = document.documentElement
  html.setAttribute('lang', baseLang)
  html.setAttribute('dir', dir)
  
  // Toggle a utility class to enable Tailwind RTL if used
  if (dir === 'rtl') {
    html.classList.add('rtl')
    html.classList.remove('ltr')
  } else {
    html.classList.add('ltr')
    html.classList.remove('rtl')
  }
}

// Apply direction on initialization
applyDirection()

// Apply direction whenever language changes
i18n.on('languageChanged', (lng) => {
  applyDirection(lng)
})

export default i18n


